import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const SchoolFees = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;
  const storedParent = useMemo(() => JSON.parse(localStorage.getItem('parentUser') || 'null'), []);
  const parent = location.state?.parent || storedParent;
  const isParentPortal = Boolean(parent);

  const [feeStructures, setFeeStructures] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFee, setSelectedFee] = useState(null);
  const [feeSelections, setFeeSelections] = useState({});
  const [showCheckout, setShowCheckout] = useState(false);
  const [studentFeeData, setStudentFeeData] = useState(null);
  const money = (value) => `NGN ${Number(value || 0).toLocaleString()}`;

  useEffect(() => {
    if (!student) {
      navigate(isParentPortal ? '/parent/dashboard' : '/student-login', isParentPortal ? { state: { parent } } : undefined);
      return;
    }
    loadData();
  }, [student, navigate, isParentPortal, parent]);

  useEffect(() => {
    // Load student's fee selections when fee structures are loaded
    if (feeStructures.length > 0) {
      loadFeeSelections();
    }
  }, [feeStructures]);

  const loadData = async () => {
    try {
      const [feeResponse, paymentResponse] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/student/${student.username}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/payments?student=${encodeURIComponent(student.username)}`)
      ]);

      const feeData = await feeResponse.json();
      const userPayments = await paymentResponse.json();

      setStudentFeeData(feeData);
      setFeeStructures((feeData.fees || []).map((fee) => ({
        id: fee.feeId,
        name: fee.feeName,
        description: `${fee.status} - balance N${Number(fee.balance || 0).toLocaleString()}`,
        amount: fee.totalAmount,
        category: 'School Fee',
        isMandatory: true,
        source: 'global'
      })));
      setPayments(userPayments);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalPaid = () => {
    return Number(studentFeeData?.summary?.totalPaid || 0);
  };

  const loadFeeSelections = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/students/fee-selections/${student.username}`);
      const selections = await response.json();

      // Convert selections array to object for easy lookup
      const selectionsObj = {};
      selections.forEach(selection => {
        selectionsObj[selection.feeId] = selection.selected;
      });

      // Default optional fees to selected (true) if not set
      feeStructures.forEach(fee => {
        if (fee.isMandatory === false && selectionsObj[fee.id] === undefined) {
          selectionsObj[fee.id] = true; // Default optional fees to selected
        }
      });

      setFeeSelections(selectionsObj);
    } catch (error) {
      console.error('Error loading fee selections:', error);
      // Set defaults for optional fees
      const defaults = {};
      feeStructures.forEach(fee => {
        if (fee.isMandatory === false) {
          defaults[fee.id] = true;
        }
      });
      setFeeSelections(defaults);
    }
  };

  const saveFeeSelections = async (selections) => {
    try {
      const selectionsArray = Object.entries(selections).map(([feeId, selected]) => ({
        feeId,
        selected
      }));

      await fetch(`${API_ENDPOINTS.BASE_URL}/api/students/fee-selections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: student.username,
          selections: selectionsArray
        })
      });
    } catch (error) {
      console.error('Error saving fee selections:', error);
    }
  };

  const handleFeeSelectionChange = async (feeId, selected) => {
    const newSelections = { ...feeSelections, [feeId]: selected };
    setFeeSelections(newSelections);
    await saveFeeSelections(newSelections);
  };

  const calculateTotalOwed = () => {
    return Number(studentFeeData?.summary?.totalExpected || 0);
  };

  const getOutstandingBalance = () => {
    return Math.max(0, calculateTotalOwed() - calculateTotalPaid());
  };

  const getMandatoryFees = () => {
    return feeStructures.filter(fee => fee.isMandatory !== false);
  };

  const getOptionalFees = () => {
    return feeStructures.filter(fee => fee.isMandatory === false);
  };

  const getSelectedFees = () => {
    return feeStructures.filter(fee => fee.isMandatory || feeSelections[fee.id]);
  };

  const handleCheckout = () => {
    const selectedFees = getSelectedFees();
    if (selectedFees.length === 0) {
      alert('Please select at least one fee to proceed with payment.');
      return;
    }
    setShowCheckout(true);
  };

  const handlePayment = async () => {
    const selectedFees = getSelectedFees();
    const totalAmount = calculateTotalOwed();

    if (selectedFees.length === 0 || totalAmount <= 0) {
      alert('No fees selected for payment.');
      return;
    }

    setSelectedFee({ name: 'Selected Fees', amount: totalAmount });
    try {
      // Create a combined fee type description
      const feeTypes = selectedFees.map(fee => fee.name).join(', ');

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/flutterwave/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: student.username,
          amount: getOutstandingBalance(),
          feeType: feeTypes,
          feeItems: selectedFees
            .map(fee => {
              const detail = studentFeeData?.fees?.find(item => item.feeId === fee.id);
              return { name: fee.name, amount: Number(detail?.balance || 0) };
            })
            .filter(item => item.amount > 0),
          paymentMethod: 'Parent Portal',
          parentId: parent?.id,
          parentName: parent?.name,
          parentEmail: parent?.email,
          parentPhone: parent?.phone
        })
      });

      const data = await response.json();

      if (response.ok && data.paymentLink) {
        window.location.href = data.paymentLink;
      } else {
        alert('Payment initialization failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to initialize payment. Please try again.');
    } finally {
      setSelectedFee(null);
      setShowCheckout(false);
    }
  };

  if (!student) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading fees...</div>
      </div>
    );
  }

  const totalPaid = calculateTotalPaid();
  const totalOwed = calculateTotalOwed();
  const outstanding = getOutstandingBalance();

  return (
    <div className="min-h-screen bg-blue-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-blue-100">
          <p className="text-blue-700 font-black uppercase text-sm">School Fees</p>
          <h1 className="text-3xl md:text-4xl font-black text-slate-950 mt-1">{student.firstName} {student.lastName}</h1>
          <p className="text-gray-600 mt-2">{student.className} · Payments are managed through parent accounts.</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-green-100 p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-green-800">Total Paid</h3>
            <p className="text-2xl font-bold text-green-600">{money(totalPaid)}</p>
          </div>
          <div className="bg-blue-100 p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-blue-800">Total Required</h3>
            <p className="text-2xl font-bold text-blue-600">{money(totalOwed)}</p>
          </div>
          <div className={`p-6 rounded-lg shadow ${outstanding > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
            <h3 className={`text-lg font-semibold ${outstanding > 0 ? 'text-red-800' : 'text-green-800'}`}>
              Outstanding Balance
            </h3>
            <p className={`text-2xl font-bold ${outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {money(outstanding)}
            </p>
          </div>
        </div>

        {/* Fee Structures */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-2xl font-bold text-blue-800 mb-6">Fee Structure</h2>

          {/* Fee Source Information */}
          {studentFeeData && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-md font-semibold text-blue-800 mb-2">Your Fee Structure</h3>
              <p className="text-sm text-blue-700 mb-3">
                Fees are calculated based on your class ({student.className}) and enrollment status.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span>Global Fees: {studentFeeData.global?.length || 0}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>Class Fees: {studentFeeData.classSpecific?.length || 0}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span>Special Fees: {studentFeeData.specialCategory?.length || 0}</span>
                </div>
              </div>
            </div>
          )}

          {/* Mandatory Fees */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-red-600 mb-4">Mandatory Fees (Required)</h3>
            <div className="space-y-3">
              {getMandatoryFees().map((fee) => (
                <div key={fee.id} className={`flex justify-between items-center p-4 border rounded-lg ${
                  fee.source === 'class' ? 'border-green-200 bg-green-50' :
                  fee.source === 'special' ? 'border-purple-200 bg-purple-50' :
                  'border-red-200 bg-red-50'
                }`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full ${
                      fee.source === 'class' ? 'bg-green-500' :
                      fee.source === 'special' ? 'bg-purple-500' :
                      'bg-red-500'
                    }`}></div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800">{fee.name}</h4>
                      <p className="text-gray-600">{fee.description}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-1 rounded text-xs ${
                          fee.source === 'class' ? 'bg-green-100 text-green-800' :
                          fee.source === 'special' ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {fee.source === 'class' ? `Class: ${fee.className}` :
                           fee.source === 'special' ? `Category: ${fee.category}` :
                           'Global'}
                        </span>
                        <span className="text-xs text-gray-500">({fee.category})</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-xl font-bold text-red-600">{money(fee.amount)}</span>
                    <span className="text-sm text-red-600 font-medium">Required</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Optional Fees */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-green-600 mb-4">Optional Fees (Choose What You Need)</h3>
            <div className="space-y-3">
              {getOptionalFees().map((fee) => (
                <div key={fee.id} className={`flex justify-between items-center p-4 border rounded-lg ${
                  fee.source === 'class' ? 'border-green-200 bg-green-50' :
                  fee.source === 'special' ? 'border-purple-200 bg-purple-50' :
                  'border-green-200 bg-green-50'
                }`}>
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={feeSelections[fee.id] || false}
                      onChange={(e) => handleFeeSelectionChange(fee.id, e.target.checked)}
                      className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <div className={`w-4 h-4 rounded-full ${
                      fee.source === 'class' ? 'bg-green-500' :
                      fee.source === 'special' ? 'bg-purple-500' :
                      'bg-blue-500'
                    }`}></div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800">{fee.name}</h4>
                      <p className="text-gray-600">{fee.description}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-1 rounded text-xs ${
                          fee.source === 'class' ? 'bg-green-100 text-green-800' :
                          fee.source === 'special' ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {fee.source === 'class' ? `Class: ${fee.className}` :
                           fee.source === 'special' ? `Category: ${fee.category}` :
                           'Global'}
                        </span>
                        <span className="text-xs text-gray-500">({fee.category})</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-xl font-bold text-green-600">{money(fee.amount)}</span>
                    <span className={`px-2 py-1 rounded text-sm ${feeSelections[fee.id] ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {feeSelections[fee.id] ? 'Selected' : 'Not Selected'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Summary */}
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-800 mb-4">Payment Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Mandatory Fees:</span>
                <span className="font-semibold">{money(getMandatoryFees().reduce((total, fee) => total + parseFloat(fee.amount), 0))}</span>
              </div>
              <div className="flex justify-between">
                <span>Selected Optional Fees:</span>
                <span className="font-semibold">{money(getOptionalFees().filter(fee => feeSelections[fee.id]).reduce((total, fee) => total + parseFloat(fee.amount), 0))}</span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between text-lg font-bold">
                <span>Total Selected:</span>
                <span>{money(totalOwed)}</span>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              <div className="text-sm text-gray-600">
                Idea You can change your optional fee selections anytime. Only selected fees will be included in your total.
              </div>
              <div className="flex justify-center">
                <button
                  onClick={handleCheckout}
                  disabled={outstanding <= 0}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg shadow-lg hover:shadow-xl transition duration-200"
                >
                  Proceed to Checkout ({money(outstanding)})
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Payment History */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold text-blue-800 mb-6">Payment History</h2>
          {payments.length === 0 ? (
            <p className="text-gray-600">No payment history found.</p>
          ) : (
            <div className="space-y-4">
              {payments.map((payment) => (
                <div key={payment.id} className="flex justify-between items-center p-4 border rounded-lg">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">{payment.feeType}</h3>
                    <p className="text-gray-600">
                      {payment.date ? new Date(payment.date).toLocaleDateString() : 'Date not available'}
                    </p>
                    <p className="text-sm text-gray-500">
                      TXN: {payment.tx_ref}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-green-600">{money(payment.amount)}</p>
                    <span className={`px-2 py-1 rounded text-sm ${
                      payment.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {payment.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Checkout Modal */}
        {showCheckout && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-blue-900">Checkout Summary</h2>
                  <button
                    onClick={() => setShowCheckout(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>

                {/* Student Info */}
                <div className="bg-blue-50 p-4 rounded-lg mb-6">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">Student Information</h3>
                  <p className="text-gray-700"><strong>Name:</strong> {student.firstName} {student.lastName}</p>
                  <p className="text-gray-700"><strong>Class:</strong> {student.className}</p>
                  <p className="text-gray-700"><strong>Username:</strong> {student.username}</p>
                </div>

                {/* Selected Fees Breakdown */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Selected Fees</h3>

                  {/* Mandatory Fees */}
                  <div className="mb-4">
                    <h4 className="text-md font-medium text-red-600 mb-2">Mandatory Fees</h4>
                    <div className="space-y-2">
                      {getMandatoryFees().map((fee) => (
                        <div key={fee.id} className="flex justify-between items-center p-3 bg-red-50 rounded border">
                          <div>
                            <span className="font-medium text-gray-800">{fee.name}</span>
                            <span className="text-sm text-gray-600 ml-2">({fee.category})</span>
                          </div>
                          <span className="font-bold text-red-600">{money(fee.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Optional Fees */}
                  <div className="mb-4">
                    <h4 className="text-md font-medium text-green-600 mb-2">Optional Fees Selected</h4>
                    <div className="space-y-2">
                      {getOptionalFees().filter(fee => feeSelections[fee.id]).map((fee) => (
                        <div key={fee.id} className="flex justify-between items-center p-3 bg-green-50 rounded border">
                          <div>
                            <span className="font-medium text-gray-800">{fee.name}</span>
                            <span className="text-sm text-gray-600 ml-2">({fee.category})</span>
                          </div>
                          <span className="font-bold text-green-600">{money(fee.amount)}</span>
                        </div>
                      ))}
                      {getOptionalFees().filter(fee => feeSelections[fee.id]).length === 0 && (
                        <p className="text-gray-500 italic">No optional fees selected</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Total Summary */}
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Payment Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Mandatory Fees:</span>
                      <span className="font-semibold">{money(getMandatoryFees().reduce((total, fee) => total + parseFloat(fee.amount), 0))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Selected Optional Fees:</span>
                      <span className="font-semibold">{money(getOptionalFees().filter(fee => feeSelections[fee.id]).reduce((total, fee) => total + parseFloat(fee.amount), 0))}</span>
                    </div>
                    <hr className="my-3" />
                    <div className="flex justify-between text-xl font-bold text-blue-600">
                      <span>Total Amount:</span>
                      <span>{money(outstanding)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Actions */}
                <div className="flex space-x-4">
                  <button
                    onClick={() => setShowCheckout(false)}
                    className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 transition duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePayment}
                    disabled={selectedFee?.name === 'Selected Fees'}
                    className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 font-medium"
                  >
                    {selectedFee?.name === 'Selected Fees' ? 'Processing Payment...' : `Pay ${money(outstanding)}`}
                  </button>
                </div>

                {/* Payment Info */}
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> You will be redirected to our secure payment processor to complete your transaction.
                    Your selections will be saved and you can modify them anytime.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="mt-8">
          <button
            onClick={() => navigate(isParentPortal ? '/parent/dashboard' : '/student-dashboard', isParentPortal ? { state: { parent } } : { state: { student } })}
            className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700"
          >
            Back to {isParentPortal ? 'Parent Dashboard' : 'Dashboard'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchoolFees;






